import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import axiosInstance, { getUserDetails, canEditSamplingFishData } from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import CustomModal from "../../components/custom/customModal";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";

import {
  CAPTURE_METHOD_OPTIONS,
  mapSamplingMethodToCaptureMethod,
  getFishAssignmentMode,
  getFishAssignmentColumnLabel,
  buildFishSamplePayload,
} from "../../utils/samplingCpueUtils";
import { displayMethodLabel, WrLabel, WsLabel } from "../../utils/samplingDisplayLabels";
import { resolveScopedFisherID } from "../../utils/pondListForUser";

const AddSamplingDetail = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const userDetails = getUserDetails();
  const canSaveSampling = canEditSamplingFishData(userDetails);
  const sessionScopedFisherID = resolveScopedFisherID(userDetails);
  const history = useHistory();
  const [lengthUnit, setLengthUnit] = useState("");
  const [weightUnit, setWeightUnit] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Data states
  const [rows, setRows] = useState([]);
  const [availableFishes, setAvailableFishes] = useState([]);
  const [pond, setPond] = useState({});
  const [hasExistingData, setHasExistingData] = useState(false);
  const [effortUnits, setEffortUnits] = useState([]);
  const [pondAnglers, setPondAnglers] = useState([]);
  const assignmentMode = getFishAssignmentMode(pond?.SamplingMethod);
  const assignmentLabel = getFishAssignmentColumnLabel(pond?.SamplingMethod);
  const sessionCaptureMethod =
    mapSamplingMethodToCaptureMethod(pond?.SamplingMethod) || "Angling";

  // Image handling state
  const [preview, setPreview] = useState(null);
  const [imageToShow, setImageToShow] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedID, setSelectedID] = useState(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch all required data
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      // 1. Fetch pond details
      const detailParams = { id };
      if (sessionScopedFisherID != null && sessionScopedFisherID !== "") {
        detailParams.FisherID = sessionScopedFisherID;
      }
      const pondResponse = await axiosInstance.get(`/api/PondDetail/GetPondDetail`, {
        params: detailParams,
      });
      const pondData = pondResponse.data;
      setPond(pondData);
      setLengthUnit(pondData.LengthUnitName);
      setWeightUnit(pondData.WeightUnitName);
      setEffortUnits(pondData.EffortUnits || []);

      if (getFishAssignmentMode(pondData.SamplingMethod) === "angler") {
        try {
          const anglerRes = await axiosInstance.get("/api/PondDetail/PondAnglers", {
            params: { PondDID: id },
          });
          setPondAnglers(anglerRes.data?.anglers || []);
        } catch {
          setPondAnglers([]);
        }
      } else {
        setPondAnglers([]);
      }

      // 2. Fetch available fish types for this pond
      const fishResponse = await axiosInstance.get(`/api/SamplingFishes/GetAllFishesBYPondDID/${id}`);
      setAvailableFishes(fishResponse.data);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, "Failed to fetch required data");
    }
  }, [id, sessionScopedFisherID]);

  const resolvedFisherID = resolveScopedFisherID(userDetails, pond);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Add first row automatically if no existing data
  useEffect(() => {
    if (!hasExistingData && rows.length === 0) {
      setRows([{
        fishTypeId: "",
        length: "",
        weight: "",
        ws: "",
        wr: "",
        tagNumber: "",
        captureMethod: sessionCaptureMethod,
        effortUnitId: "",
        anglerStaffId: "",
        status: "Culled",
        equation: ""
      }]);
    }
  }, [hasExistingData, rows.length, pond?.SamplingMethod]);

  // Image handling functions
  const openImageModal = (id) => {
    setSelectedID(id);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
    setPreview(null);
    setSelectedImage(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 4 * 1024 * 1024) {
        showErrorToast("Image size should be less than 4MB");
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const showImagePreview = (blobData) => {
    setModalVisible(true);
    const imageData = blobData.data;
    const base64Image = `data:image/jpeg;base64,${Buffer.from(imageData).toString("base64")}`;
    setImageToShow(base64Image);
  };

  const handleImageUpload = async () => {
    if (!selectedImage) {
      showErrorToast("Please select an image first");
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('image', selectedImage);

    try {
      await axiosInstance.post(`/api/FishSampling/AddImageToSample/${selectedID}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showSuccessToast('Image uploaded successfully');
      fetchData();
    } catch (error) {
      handleApiError(error, "Failed to upload image");
    } finally {
      closeImageModal();
      setIsLoading(false);
    }
  };

  // Row manipulation
  const addRow = (fishTypeId = "") => {
    setRows([...rows, {
      fishTypeId,
      length: "",
      weight: "",
      ws: "",
      wr: "",
      tagNumber: "",
      captureMethod: sessionCaptureMethod,
      effortUnitId: "",
      anglerStaffId: "",
      status: "Culled",
      equation: fishTypeId ? availableFishes.find(fish => fish.FishTypeID == fishTypeId)?.[
        pond.LengthUnitName === "mm" ? "WsMGEquation" : "WsIPEquation"
      ] || "" : "",
      fishTypeName: fishTypeId ? availableFishes.find(fish => fish.FishTypeID == fishTypeId)?.FishTypeName || "" : ""
    }]);
  };

  const removeRow = (index) => {
    if (rows.length <= 1) {
      showErrorToast("At least one row is required");
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];

    console.log(updatedRows);
    let newValue = value;

    // For length and weight, store the raw string value to allow partial decimal inputs
    if (field === "length" || field === "weight") {
      if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
        newValue = value;
      } else {
        return;
      }
    }

    updatedRows[index] = { ...updatedRows[index], [field]: newValue };

    if (field === "fishTypeId") {
      const selectedFish = availableFishes.find(fish => fish.FishTypeID == value);
      updatedRows[index].equation = pond.LengthUnitName === "mm"
        ? selectedFish?.WsMGEquation || ""
        : selectedFish?.WsIPEquation || "";

      updatedRows[index].fishTypeName = selectedFish?.FishTypeName || "";
    }

    if (field === "fishTypeId" || field === "length" || field === "weight") {
      updatedRows[index] = calculateWsWr(updatedRows, index);
    }

    setRows(updatedRows);
  };

  const handleWeightBlur = (index) => {
    const currentRow = rows[index];
    const parsedLength = parseFloat(currentRow.length);
    const parsedWeight = parseFloat(currentRow.weight);

    if (
      currentRow.fishTypeId &&
      !isNaN(parsedLength) && parsedLength > 0 &&
      !isNaN(parsedWeight) && parsedWeight > 0
    ) {
      // Update the row with the parsed number to ensure consistent formatting
      const updatedRows = [...rows];
      updatedRows[index].weight = parsedWeight.toFixed(4);
      setRows(updatedRows);

      // Check if an empty row already exists
      const hasEmptyRow = rows.some(row =>
        !row.fishTypeId || row.length === "" || row.weight === ""
      );

      // Only add a new row if no empty row exists
      if (!hasEmptyRow) {
        addRow(currentRow.fishTypeId);
      }
    }
  };

  const calculateWsWr = (rowsArray, index) => {
    const row = { ...rowsArray[index] };
    const length = parseFloat(row.length);
    const weight = parseFloat(row.weight);

    if (!row.equation || isNaN(length) || length <= 0) {
      return { ...row, ws: "", wr: "" };
    }

    try {
      const replacedExpression = row.equation.replace(/L/g, length).replace(/\^/g, '**');

      if (!/^[0-9+\-*/(). L^log]*$/.test(row.equation)) {
        console.error("Invalid equation format:", row.equation);
        return { ...row, ws: "", wr: "" };
      }

      console.log(replacedExpression);

      const calculatedWs = Function(`return ${replacedExpression}`)();

      if (isNaN(calculatedWs) || !isFinite(calculatedWs)) {
        return { ...row, ws: "", wr: "" };
      }

      if (calculatedWs > 0) {
        const ws = parseFloat(calculatedWs.toFixed(3));
        const wr = !isNaN(weight) && weight > 0
          ? parseFloat((weight / calculatedWs).toFixed(3))
          : "";

        return { ...row, ws, wr };
      } else {
        return { ...row, ws: "", wr: "" };
      }
    } catch (error) {
      console.error("Calculation error:", error, { equation: row.equation, length, weight });
      return { ...row, ws: "", wr: "" };
    }
  };

  const validateData = () => {
    const hasValidRow = rows.some(row =>
      row.fishTypeId && row.length > 0 && row.weight > 0
    );

    if (!hasValidRow) {
      showErrorToast("Please provide at least one complete sample");
      return false;
    }

    if (assignmentMode === "effort_unit") {
      if (!effortUnits.length) {
        showErrorToast(
          "Configure areas, nets, or hauls on the General tab before entering fish samples"
        );
        return false;
      }
      const missingEffort = rows.some(
        (row) =>
          row.fishTypeId &&
          row.length > 0 &&
          row.weight > 0 &&
          !row.effortUnitId
      );
      if (missingEffort) {
        showErrorToast(`Please select a ${assignmentLabel.toLowerCase()} for each fish`);
        return false;
      }
    }

    if (assignmentMode === "angler") {
      if (!pondAnglers.length) {
        showErrorToast(
          "No anglers selected for this session. Choose anglers on the General tab before entering fish samples"
        );
        return false;
      }
      const missingAngler = rows.some(
        (row) =>
          row.fishTypeId &&
          row.length > 0 &&
          row.weight > 0 &&
          !row.anglerStaffId
      );
      if (missingAngler) {
        showErrorToast("Please select an angler for each fish");
        return false;
      }
    }

    return true;
  };

  const saveData = async () => {
    if (!validateData()) return;

    const payload = rows
      .filter(row => row.fishTypeId && row.length > 0 && row.weight > 0)
      .map(row =>
        buildFishSamplePayload({
          PondDID: id,
          FishTypeID: row.fishTypeId,
          FisherID: resolvedFisherID ?? pond?.FisherID,
          Length: parseFloat(row.length).toFixed(4),
          Weight: parseFloat(row.weight).toFixed(4),
          Ws: parseFloat(row.ws || 0).toFixed(4),
          Wr: parseFloat(row.wr || 0).toFixed(4),
          TagNumber: row.tagNumber || '',
          CaptureMethod:
            assignmentMode === "capture_method"
              ? row.captureMethod
              : sessionCaptureMethod,
          ...(assignmentMode === "effort_unit"
            ? { EffortUnitID: row.effortUnitId }
            : {}),
          ...(assignmentMode === "angler"
            ? { AnglerStaffID: row.anglerStaffId }
            : {}),
          Status: row.status,
          ...(row.image && { Image: row.image }),
        })
      );

    setIsLoading(true);
    try {
      await axiosInstance.post("/api/FishSampling/AddSamples", payload);
      showSuccessToast("Data saved successfully");
      history.push("/samplingViaPondDetail/" + id);
    } catch (error) {
      handleApiError(error, "Failed to save data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApiError = (error, defaultMessage) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      window.location.reload();
      showErrorToast("Session expired. Please log in again.");
      return;
    }

    const errorMessage = error.response?.data?.message || defaultMessage;
    showErrorToast(errorMessage);
    console.error(errorMessage, error);
  };

  const renderAssignmentField = (row, index, sizeClass = "") => {
    if (assignmentMode === "effort_unit") {
      return (
        <select
          className={`form-control ${sizeClass}`.trim()}
          value={row.effortUnitId || ""}
          onChange={(e) => handleInputChange(index, "effortUnitId", e.target.value)}
        >
          <option value="">Select…</option>
          {effortUnits.map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.Label}
            </option>
          ))}
        </select>
      );
    }
    if (assignmentMode === "angler") {
      return (
        <select
          className={`form-control ${sizeClass}`.trim()}
          value={row.anglerStaffId || ""}
          onChange={(e) => handleInputChange(index, "anglerStaffId", e.target.value)}
        >
          <option value="">Select angler…</option>
          {pondAnglers.map((angler) => (
            <option key={angler.staffId} value={angler.staffId}>
              {angler.fullName}
            </option>
          ))}
        </select>
      );
    }
    return (
      <select
        className={`form-control ${sizeClass}`.trim()}
        value={row.captureMethod || "Angling"}
        onChange={(e) => handleInputChange(index, "captureMethod", e.target.value)}
      >
        {CAPTURE_METHOD_OPTIONS.map((method) => (
          <option key={method} value={method}>
            {displayMethodLabel(method)}
          </option>
        ))}
      </select>
    );
  };

  return (
    isLoading ? <Preloader /> :
      <div className="container-fluid p-2 p-md-3">
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
          <div className="mb-3 mb-md-0 flex-grow-1" style={{ minWidth: 0 }}>
            <PondDetailWorkflowPageHeader
              pondDetailId={id}
              section="sampling"
              detailName={pond?.Name}
              noBottomMargin
            />
          </div>
          <div className="ms-md-auto">
            {canSaveSampling && (
              <button
                type="button"
                onClick={saveData}
                className="btn btn-success btn-rounded px-3 px-md-4 rounded-pill"
              >
                <i className="fa fa-save me-1"></i>
                {windowWidth > 576 ? "Save" : "Save"}
              </button>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="card border-0 shadow-sm mb-4 donezo-workflow-tabs">
          <ul className="nav nav-tabs border-0 px-3 pt-3" style={{ borderBottom: '2px solid #e9ecef' }}>
            <li className="nav-item me-2">
              <Link 
                to={"/UpdatePD/" + id} 
                className="nav-link px-3 py-2 rounded-top"
                style={{ 
                  color: '#6c757d',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}
              >
                <i className="fas fa-cog me-2" aria-hidden />General
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link 
                to={"/pondFishViaPondDetail/" + id} 
                className="nav-link px-3 py-2 rounded-top"
                style={{ 
                  color: '#6c757d',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}
              >
                <i className="fas fa-fish me-2" aria-hidden />{windowWidth > 768 ? "Fish Sampled/Caught" : "Fish"}
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link 
                to={"/samplingViaPondDetail/" + id} 
                className="nav-link px-3 py-2 rounded-top"
                style={{ 
                  color: '#348fe2',
                  backgroundColor: '#ffffff',
                  border: '2px solid #348fe2',
                  borderBottom: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}
              >
                <i className="fas fa-chart-line me-2" aria-hidden />{windowWidth > 768 ? "Sampling Detail" : "Samples"}
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={"/FishDataTableView/" + id}
                className="nav-link px-3 py-2 rounded-top"
                style={{
                  color: '#6c757d',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                }}
              >
                <i className="fas fa-table me-2" aria-hidden />Table view
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={"/fish-data-graph-new/" + id}
                className="nav-link px-3 py-2 rounded-top"
                style={{
                  color: '#6c757d',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                }}
              >
                <i className="fas fa-chart-area me-2" aria-hidden />Graph view
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link 
                to={"/pondNoteViaPondDetail/" + id} 
                className="nav-link px-3 py-2 rounded-top"
                style={{ 
                  color: '#6c757d',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}
              >
                <i className="fas fa-note-sticky me-2" aria-hidden />Notes
              </Link>
            </li>
          </ul>
        </div>

        {/* Tab Content */}
        <div className="card border-0 shadow-sm donezo-workflow-card">
          <div className="tab-content p-3 p-md-4">
            <div className="tab-pane fade show active" id="allTab">
              {/* Desktop Table */}
              <div className="d-none d-md-block">
                <div className="table-responsive mb-3">
                  <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Fish Type</th>
                        <th>Length ({lengthUnit})</th>
                        <th>Weight ({weightUnit})</th>
                        <th>
                          <WsLabel />
                        </th>
                        <th>
                          <WrLabel />
                        </th>
                        <th>Tag Number</th>
                        <th>{assignmentLabel}</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={index}>
                          <td>
                            {hasExistingData ? (
                              <input
                                type="text"
                                className="form-control"
                                value={row.fishTypeName}
                                readOnly
                              />
                            ) : (
                              <select
                                className="form-control"
                                value={row.fishTypeId}
                                onChange={(e) => handleInputChange(index, "fishTypeId", e.target.value)}
                                required
                              >
                                <option value="">Select Fish</option>
                                {availableFishes.map(fish => (
                                  <option key={fish.FishTypeID} value={fish.FishTypeID}>
                                    {fish.FishTypeName}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              value={row.length}
                              onChange={(e) => handleInputChange(index, "length", e.target.value)}
                              placeholder="Enter length"
                              required
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              value={row.weight}
                              onChange={(e) => handleInputChange(index, "weight", e.target.value)}
                              onBlur={() => handleWeightBlur(index)}
                              placeholder="Enter weight"
                              required
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              value={row.ws}
                              readOnly
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              value={row.wr}
                              readOnly
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              value={row.tagNumber}
                              onChange={(e) => handleInputChange(index, "tagNumber", e.target.value)}
                              placeholder="Tag Number"
                            />
                          </td>
                          <td>
                            {renderAssignmentField(row, index)}
                          </td>
                          <td>
                            <select
                              className="form-control"
                              value={row.status}
                              onChange={(e) => handleInputChange(index, "status", e.target.value)}
                            >
                              <option value="Culled">Culled</option>
                              <option value="Released">Released</option>
                            </select>
                          </td>
                          <td>
                            {hasExistingData && row.image && (
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => showImagePreview(row.image)}
                                title="View image"
                              >
                                <i className="fa fa-eye"></i>
                              </button>
                            )}
                            {hasExistingData && (
                              <button
                                className="btn btn-sm btn-outline-secondary me-2"
                                onClick={() => openImageModal(row.id)}
                                title="Upload image"
                              >
                                <i className="fa fa-upload"></i>
                              </button>
                            )}
                            {(!hasExistingData || rows.length > 1) && (
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => removeRow(index)}
                                title="Remove row"
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="d-md-none">
                {rows.map((row, index) => (
                  <div key={index} className="card mb-3 shadow-sm">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                          {hasExistingData ? (
                            <span>{row.fishTypeName}</span>
                          ) : (
                            <select
                              className="form-control form-control-sm"
                              value={row.fishTypeId}
                              onChange={(e) => handleInputChange(index, "fishTypeId", e.target.value)}
                              required
                            >
                              <option value="">Select Fish</option>
                              {availableFishes.map(fish => (
                                <option key={fish.FishTypeID} value={fish.FishTypeID}>
                                  {fish.FishTypeName}
                                </option>
                              ))}
                            </select>
                          )}
                        </h6>
                        <div>
                          {hasExistingData && row.image && (
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => showImagePreview(row.image)}
                              title="View image"
                            >
                              <i className="fa fa-eye"></i>
                            </button>
                          )}
                          {hasExistingData && (
                            <button
                              className="btn btn-sm btn-outline-secondary me-1"
                              onClick={() => openImageModal(row.id)}
                              title="Upload image"
                            >
                              <i className="fa fa-upload"></i>
                            </button>
                          )}
                          {(!hasExistingData || rows.length > 1) && (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => removeRow(index)}
                              title="Remove row"
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-6 mb-2">
                          <small className="text-muted">Length ({lengthUnit})</small>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={row.length}
                            onChange={(e) => handleInputChange(index, "length", e.target.value)}
                            placeholder="Length"
                            required
                          />
                        </div>
                        <div className="col-6 mb-2">
                          <small className="text-muted">Weight ({weightUnit})</small>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={row.weight}
                            onChange={(e) => handleInputChange(index, "weight", e.target.value)}
                            onBlur={() => handleWeightBlur(index)}
                            placeholder="Weight"
                            required
                          />
                        </div>
                        <div className="col-6 mb-2">
                          <small className="text-muted">Ws</small>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={row.ws}
                            readOnly
                          />
                        </div>
                        <div className="col-6 mb-2">
                          <small className="text-muted">Wr</small>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={row.wr}
                            readOnly
                          />
                        </div>
                        <div className="col-6 mb-2">
                          <small className="text-muted">Tag Number</small>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={row.tagNumber}
                            onChange={(e) => handleInputChange(index, "tagNumber", e.target.value)}
                            placeholder="Tag Number"
                          />
                        </div>
                        <div className="col-6 mb-2">
                          <small className="text-muted">{assignmentLabel}</small>
                          {renderAssignmentField(row, index, "form-control-sm")}
                        </div>
                        <div className="col-12 mb-2">
                          <small className="text-muted">Status</small>
                          <select
                            className="form-control form-control-sm"
                            value={row.status}
                            onChange={(e) => handleInputChange(index, "status", e.target.value)}
                          >
                            <option value="Culled">Culled</option>
                            <option value="Released">Released</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-end mb-3">
                <button
                  type="button"
                  className="btn btn-primary btn-sm rounded-pill px-3"
                  onClick={() => addRow()}
                >
                  <i className="fa fa-plus me-1" aria-hidden />
                  {windowWidth > 576 ? "Add Row" : "Add"}
                </button>
              </div>
            </div>
          </div>
        </div>
      

        {/* Image Preview Modal */}
        <CustomModal
          isOpen={modalVisible}
          onClose={() => setModalVisible(false)}
          title="Fish Image"
          showButton={false}
          modalSize="modal-lg"
          minHeight="600px"
        >
          <div style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
          }}>
            <img
              src={imageToShow}
              alt="Fish"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </div>
        </CustomModal>

        {/* Image Upload Modal */}
        <CustomModal
          isOpen={isModalOpen}
          onSubmit={handleImageUpload}
          onClose={closeImageModal}
          title="Upload Fish Image"
          showButton={true}
          showCancelButton={true}
          showSubmitButton={true}
          modalSize="modal-lg"
          minHeight="auto"
        >
          <span className="text-danger">(Size Should be Lower than 4MB)</span>
          <div className="mt-3">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="form-control"
            />
            {preview && (
              <div className="row pt-3">
                <img
                  src={preview}
                  alt="Selected Preview"
                  style={{
                    width: "100%",
                    maxHeight: "400px",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
          </div>
        </CustomModal>
      </div>
  );
};

export default AddSamplingDetail;